/**
 * The times of the fixed texts of a music video of the editor (spec 2026-10-09 music video editor
 * §4.5): ONE rule for the web preview and the server render, so the made video shows the texts when
 * the preview shows them. All times are video seconds (0 = `settings.startSec`).
 *
 * - The title holds from 0 to the first downbeat at or after 4 s (`musicVideoTitleEndSec`).
 * - The name shows for `MUSIC_VIDEO_NAME_SEC` (6 s) after the title ends.
 * - The end card takes the last 20 s from a downbeat (`longVideoEndCardStartSec`, the shared rule of
 *   the long videos); a video shorter than 40 s has no end card.
 *
 * Pure.
 */
import { longVideoEndCardStartSec } from './story/longVideoEndCard';
import { musicVideoTitleEndSec } from './story/musicVideoLayout';

/** How long the name shows after the title. */
export const MUSIC_VIDEO_NAME_SEC = 6;

export interface MusicVideoTextSpan {
  startSec: number;
  endSec: number;
}

export interface MusicVideoTextTimes {
  title: MusicVideoTextSpan;
  name: MusicVideoTextSpan;
  /** Null: the video is too short for the end card. */
  endCard: MusicVideoTextSpan | null;
}

const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * The times of the title, the name and the end card of a video of `lengthSec` seconds whose
 * downbeats (video seconds, sorted) are `downbeats`. The name never runs into the end card or past
 * the end. Pure.
 */
export function musicVideoTextTimes(input: { downbeats: readonly number[]; lengthSec: number }): MusicVideoTextTimes {
  const lengthSec = Math.max(0, input.lengthSec);
  const titleEnd = Math.min(lengthSec, musicVideoTitleEndSec(input.downbeats));
  const endCardStart = longVideoEndCardStartSec(lengthSec, input.downbeats);
  const nameLimit = endCardStart ?? lengthSec;
  return {
    title: { startSec: 0, endSec: round3(titleEnd) },
    name: {
      startSec: round3(titleEnd),
      endSec: round3(Math.max(titleEnd, Math.min(nameLimit, titleEnd + MUSIC_VIDEO_NAME_SEC))),
    },
    endCard: endCardStart === null ? null : { startSec: endCardStart, endSec: round3(lengthSec) },
  };
}
